"use client";

import { useState } from "react";
import {
  Checkbox,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
} from "@/components/tool-kit/styles";
import {
  OutputBox,
  type OutputStrings,
  PanelHeading,
} from "@/components/tools/random-fun/discord-output";
import {
  PreviewAnsi,
  PreviewShell,
} from "@/components/tools/random-fun/discord-preview";
import { t } from "@/lib/i18n/interpolate";
import {
  ANSI_BACKGROUNDS,
  ANSI_FOREGROUNDS,
  type AnsiSegment,
  ansiBlock,
} from "@/lib/tool-kit/random-fun/discord-markdown";

/** One editable piece of coloured text. */
export interface SegmentRow extends AnsiSegment {
  id: number;
}

/** The strings the editor draws, supplied by the widget. */
export interface AnsiEditorStrings {
  segmentText: string;
  textColour: string;
  background: string;
  defaultColour: string;
  bold: string;
  underline: string;
  remove: string;
  removeLabel: string;
  add: string;
  segmentLabel: string;
  foregroundNames: Record<string, string>;
  backgroundNames: Record<string, string>;
}

export const MAX_SEGMENTS = 12;

export function AnsiEditor({
  rows,
  strings,
  onChange,
  onAdd,
  onRemove,
}: {
  rows: readonly SegmentRow[];
  strings: AnsiEditorStrings;
  onChange: (id: number, patch: Partial<AnsiSegment>) => void;
  onAdd: () => void;
  onRemove: (id: number) => void;
}) {
  return (
    <div className="space-y-4">
      {rows.map((row, index) => {
        const label = t(strings.segmentLabel, { number: index + 1 });
        return (
          <fieldset className={`${CARD} space-y-3`} key={row.id}>
            <legend className="sr-only">{label}</legend>
            <TextField
              id={`dtf-seg-${row.id}`}
              label={`${label}: ${strings.segmentText}`}
              onChange={(value) => onChange(row.id, { text: value })}
              value={row.text}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                id={`dtf-fg-${row.id}`}
                label={strings.textColour}
                onChange={(value) =>
                  onChange(row.id, {
                    foreground: value === "" ? null : Number(value),
                  })
                }
                options={[
                  { value: "", label: strings.defaultColour },
                  ...ANSI_FOREGROUNDS.map((code) => ({
                    value: String(code),
                    label:
                      strings.foregroundNames[String(code)] ?? String(code),
                  })),
                ]}
                value={row.foreground === null ? "" : String(row.foreground)}
              />
              <SelectField
                id={`dtf-bg-${row.id}`}
                label={strings.background}
                onChange={(value) =>
                  onChange(row.id, {
                    background: value === "" ? null : Number(value),
                  })
                }
                options={[
                  { value: "", label: strings.defaultColour },
                  ...ANSI_BACKGROUNDS.map((code) => ({
                    value: String(code),
                    label:
                      strings.backgroundNames[String(code)] ?? String(code),
                  })),
                ]}
                value={row.background === null ? "" : String(row.background)}
              />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Checkbox
                checked={row.bold}
                label={strings.bold}
                onChange={(bold) => onChange(row.id, { bold })}
              />
              <Checkbox
                checked={row.underline}
                label={strings.underline}
                onChange={(underline) => onChange(row.id, { underline })}
              />
              {rows.length > 1 ? (
                <button
                  aria-label={t(strings.removeLabel, { number: index + 1 })}
                  className={BUTTON_SMALL}
                  onClick={() => onRemove(row.id)}
                  type="button"
                >
                  {strings.remove}
                </button>
              ) : null}
            </div>
          </fieldset>
        );
      })}
      {rows.length < MAX_SEGMENTS ? (
        <button className={BUTTON_SECONDARY} onClick={onAdd} type="button">
          {strings.add}
        </button>
      ) : null}
    </div>
  );
}

export interface AnsiPanelStrings extends AnsiEditorStrings, OutputStrings {
  ansiHeading: string;
  ansiNote: string;
  escapeNote: string;
  previewLabel: string;
  previewNote: string;
  sampleOne: string;
  sampleTwo: string;
}

const PLAIN: AnsiSegment = {
  text: "",
  foreground: null,
  background: null,
  bold: false,
  underline: false,
};

/** The coloured-text builder: pieces of text, their colours, the preview and the result. */
export function AnsiPanel({ strings }: { strings: AnsiPanelStrings }) {
  const [rows, setRows] = useState<SegmentRow[]>([
    { ...PLAIN, id: 1, text: strings.sampleOne, foreground: 31, bold: true },
    { ...PLAIN, id: 2, text: strings.sampleTwo, foreground: 34 },
  ]);
  const [nextId, setNextId] = useState(3);

  function change(id: number, patch: Partial<AnsiSegment>) {
    setRows(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }

  return (
    <div className="space-y-5">
      <PanelHeading>{strings.ansiHeading}</PanelHeading>
      <p className={HINT}>{strings.ansiNote}</p>
      <AnsiEditor
        onAdd={() => {
          setRows([...rows, { ...PLAIN, id: nextId }]);
          setNextId(nextId + 1);
        }}
        onChange={change}
        onRemove={(id) => setRows(rows.filter((row) => row.id !== id))}
        rows={rows}
        strings={strings}
      />
      <PreviewShell label={strings.previewLabel}>
        <PreviewAnsi segments={rows} />
      </PreviewShell>
      <p className={HINT}>{strings.previewNote}</p>
      <OutputBox
        copyText={ansiBlock(rows)}
        id="dtf-ansi-output"
        shown={ansiBlock(rows, true)}
        strings={strings}
      />
      <p className={HINT}>{strings.escapeNote}</p>
    </div>
  );
}
